/* Common Imports */

import { styled } from "@mui/system";
import { useRouter } from "next/router";
import React from "react";

/* Component Imports */

import { Typography } from "@mui/material";
import { useSelector } from "react-redux";
import StateInterface from "../../../../../redux-magic/state-interface";

/*

& The listing's views and leads on its own page, for its team only (Listings #46, the app's SppSummaryCard).
& properties/cards/property-stats answers only the listing's team (and the leads count only with the Leads module),
& so this asks it and shows nothing when it refuses: no client-side guess about who is on the team.
& Leads opens the dashboard's leads of this listing, and a running AI Boost says so with "See details" (3 Nov 2026, the
& app's SppSummaryCard), on the dashboard of the business the viewer acts as when it owns the listing.

*/

const MainContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	gap: "0.75rem",
	padding: "1rem",
	background: theme.palette.background.paper,
	borderRadius: "16px",
}));

const StatsRow = styled("div")({
	display: "flex",
	flexDirection: "row",
	gap: "0.75rem",
	width: "100%",
});

const StatBox = styled("div")({
	display: "flex",
	flexDirection: "column",
	flex: 1,
	padding: "0.75rem",
	borderRadius: "12px",
	background: "#693A700A",
});

const ListingStatsSummary = ({
	property_id,
	owner_business_profile_id = "",
	boost_active = false,
	updated_at = "",
}: {
	property_id: string;
	/* The business that owns the listing: the links need the viewer to be acting as it. */
	owner_business_profile_id?: string;
	boost_active?: boolean;
	/* "Last updated on …" (3 Nov 2026, the app's listing page), for the team. */
	updated_at?: string;
}) => {
	const router = useRouter();
	const profile_context = useSelector((state: { globalState: StateInterface }) => state.globalState?.profile_context);
	/* The viewer's own dashboard, when they act as the business that owns this listing. */
	const dashboardPath: string =
		profile_context?.is_business_profile &&
		owner_business_profile_id &&
		String(profile_context.business_profile_id) === owner_business_profile_id &&
		profile_context.page_type &&
		profile_context.page_slug
			? `/${router.locale || "en-IN"}/${profile_context.page_type}s/${profile_context.page_slug}`
			: "";
	/* Arabic and Kannada beside English, as the screens around this one write them (router.locale). */
	const tr = (en: string, ar: string, kn: string): string =>
		router.locale === "ar-AE" ? ar : router.locale === "kn-IN" ? kn : en;

	const [stats, setStats] = React.useState<{ views_count: number; leads_count: number | null } | null>(null);

	React.useEffect(() => {
		setStats(null);
		if (!property_id) return;
		let active = true;
		fetch(`${process.env.PRODUCTION_API_URL}properties/cards/property-stats`, {
			method: "POST",
			cache: "no-store",
			headers: { "Content-Type": "text/plain" },
			body: JSON.stringify({ _id: property_id }),
		})
			.then((res) => (res.ok ? res.json() : null))
			.then((data) => {
				if (active && data?.status === "success") setStats(data.payload);
			})
			.catch(() => undefined);
		return () => {
			active = false;
		};
	}, [property_id]);

	/* Not in "Preview as buyer" (a buyer never sees it). */
	if (!stats || router.query.preview === "buyer") return null;

	return (
		<MainContainer>
			<Typography sx={{ fontSize: { xs: "1rem", md1190: "1.125rem" }, fontWeight: 500 }}>
				{tr("Listing performance", "أداء العقار", "ಪಟ್ಟಿಯ ಕಾರ್ಯಕ್ಷಮತೆ")}
			</Typography>
			<StatsRow>
				<StatBox>
					<Typography sx={{ fontSize: "1.25rem", fontWeight: 600 }}>{stats.views_count || 0}</Typography>
					<Typography sx={{ fontSize: "0.8125rem", color: "#666666" }}>{tr("Views", "المشاهدات", "ವೀಕ್ಷಣೆಗಳು")}</Typography>
				</StatBox>
				{stats.leads_count !== null && stats.leads_count !== undefined ? (
					<StatBox
						role={dashboardPath ? "link" : undefined}
						tabIndex={dashboardPath ? 0 : undefined}
						onClick={() => (dashboardPath ? router.push(`${dashboardPath}?screen=leads&property_id=${property_id}`) : null)}
						onKeyDown={(event) => event.key === "Enter" && dashboardPath && router.push(`${dashboardPath}?screen=leads&property_id=${property_id}`)}
						sx={{ cursor: dashboardPath ? "pointer" : "default" }}
					>
						<Typography sx={{ fontSize: "1.25rem", fontWeight: 600 }}>{stats.leads_count}</Typography>
						<Typography sx={{ fontSize: "0.8125rem", color: "#666666" }}>{tr("Leads", "العملاء المحتملون", "ಲೀಡ್‌ಗಳು")}</Typography>
					</StatBox>
				) : null}
			</StatsRow>
			{boost_active ? (
				<Typography sx={{ fontSize: "0.8125rem", color: "#693A70" }}>
					{tr("AI Boost is active", "تعزيز الذكاء الاصطناعي نشط", "AI ಬೂಸ್ಟ್ ಸಕ್ರಿಯವಾಗಿದೆ")}
					{dashboardPath ? (
						<>
							{" · "}
							<span
								role="link"
								tabIndex={0}
								onClick={() => router.push(`${dashboardPath}?screen=ai-boosts-details&property_id=${property_id}`)}
								onKeyDown={(event) =>
									event.key === "Enter" && router.push(`${dashboardPath}?screen=ai-boosts-details&property_id=${property_id}`)
								}
								style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
							>
								{tr("See details", "عرض التفاصيل", "ವಿವರಗಳನ್ನು ನೋಡಿ")}
							</span>
						</>
					) : null}
				</Typography>
			) : null}
			{updated_at && !isNaN(new Date(updated_at).getTime()) ? (
				<Typography sx={{ fontSize: "0.75rem", color: "#757575" }}>
					{tr("Last updated on ", "آخر تحديث في ", "ಕೊನೆಯ ನವೀಕರಣ ")}
					{new Date(updated_at).toLocaleDateString(router.locale || "en-IN", { day: "numeric", month: "short", year: "numeric" })}
				</Typography>
			) : null}
		</MainContainer>
	);
};

export default ListingStatsSummary;
